import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  hugsHeightAxis,
  textHugsWidth,
  type Doc,
  type Layer,
} from "@hightouch/lib/ad-studio/html-editor/model";
import { docRootStyle, renderDocLayers } from "../doc-renderer";
import { docMotionGeometry, type LayerSize } from "./doc-motion-geometry";

interface Measured {
  doc: Doc;
  sizes: ReadonlyMap<string, LayerSize>;
}

function needsMeasurement(layers: readonly Layer[]): boolean {
  return layers.some(
    (layer) =>
      textHugsWidth(layer) ||
      hugsHeightAxis(layer) ||
      (layer.type === "group" && needsMeasurement(layer.children)),
  );
}

/** Measure resting layout separately so animation never feeds back into its own bounds. */
export function useDocMotionGeometry(doc: Doc | undefined, className?: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<Measured>();
  // Lets `measure` set a plain value: React re-runs a functional updater on
  // every rebase over a skipped lower-priority update, so one that allocates
  // would bust the geometry memo each render and loop.
  const measuredRef = useRef<Measured>();
  const measurement = useMemo(
    () =>
      doc && needsMeasurement(doc.layers) ? (
        <div
          ref={ref}
          className={className}
          aria-hidden
          style={{
            ...docRootStyle(doc),
            position: "absolute",
            left: -100000,
            visibility: "hidden",
            pointerEvents: "none",
          }}
        >
          {renderDocLayers(doc, {
            layerProps: (layer) => ({ "data-motion-measure": layer.id }),
          })}
        </div>
      ) : null,
    [doc, className],
  );

  useLayoutEffect(() => {
    if (!doc || !ref.current) return;
    const elements = Array.from(
      ref.current.querySelectorAll<HTMLElement>("[data-motion-measure]"),
    );
    const measure = () => {
      const sizes = new Map(
        elements.flatMap((element) => {
          const id = element.getAttribute("data-motion-measure");
          return id === null
            ? []
            : [
                [
                  id,
                  { width: element.offsetWidth, height: element.offsetHeight },
                ] as const,
              ];
        }),
      );
      const previous = measuredRef.current;
      if (
        previous?.doc === doc &&
        Array.from(sizes).every(
          ([id, size]) =>
            previous.sizes.get(id)?.width === size.width &&
            previous.sizes.get(id)?.height === size.height,
        )
      ) {
        return;
      }
      const next = { doc, sizes };
      measuredRef.current = next;
      setMeasured(next);
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    elements.forEach((element) => observer?.observe(element));
    return () => observer?.disconnect();
  }, [doc]);
  const geometry = useMemo(
    () =>
      doc
        ? docMotionGeometry(
            doc,
            measured?.doc === doc ? measured.sizes : undefined,
          )
        : undefined,
    [doc, measured],
  );
  return { geometry, measurement };
}
